// TelcoTrack — D5 service paths and opposite-end labels for cassette occupancy.

import type { RozvadecWeldLayout } from './rozvadec-weld-layout.types';
import {
  cassetteIdsForEndpoint,
  layoutPatchConnections,
  layoutSplitterPaths,
  layoutWeldConnections,
  occupancyEndpointId,
  occupancySameConnectorChannel,
  parseOccupancyEndpointId,
} from './rozvadec-occupancy-connection';
import type {
  OccupancyCableFiberEndpoint,
  OccupancyCableRole,
  OccupancyConnectionIssueCode,
  OccupancyEndpoint,
  OccupancyPatchConnection,
  OccupancyServicePath,
  OccupancyWeldConnection,
} from './rozvadec-occupancy-connection.types';

export function cassetteIdsForConnection(
  layout: RozvadecWeldLayout,
  connectionId: string,
): string[] {
  const weld = layoutWeldConnections(layout).find((item) => item.id === connectionId);
  const patch = layoutPatchConnections(layout).find((item) => item.id === connectionId);
  const ids = new Set<string>();
  for (const end of [weld?.fromEndpointId, weld?.toEndpointId, patch?.fromEndpointId, patch?.toEndpointId]) {
    for (const cassetteId of cassetteIdsForEndpoint(parseOccupancyEndpointId(end))) {
      ids.add(cassetteId);
    }
  }
  const path = layoutSplitterPaths(layout).find((item) => item.id === connectionId);
  if (path) {
    ids.add(path.cassetteId);
    const related = [path.inputPatchId, ...path.outputPatches.map((item) => item.patchId)];
    for (const patchId of related) {
      if (!patchId) continue;
      for (const extra of cassetteIdsForConnection(layout, patchId)) ids.add(extra);
    }
  }
  return [...ids];
}

export function deriveOccupancyServicePaths(layout: RozvadecWeldLayout): OccupancyServicePath[] {
  const welds = layoutWeldConnections(layout);
  const patches = layoutPatchConnections(layout);
  const paths: OccupancyServicePath[] = [];
  for (const path of layoutSplitterPaths(layout)) {
    const inPatch = patches.find((item) => item.id === path.inputPatchId) ?? null;
    for (const out of path.outputPatches) {
      const outPatch = patches.find((item) => item.id === out.patchId) ?? null;
      const sc = otherEnd(inPatch, (end) => end.kind === 'cassette-connector');
      const lc = otherEnd(outPatch, (end) => end.kind === 'cassette-connector');
      const incoming = weldTouching(welds, sc);
      const outgoing = weldTouching(welds, lc, 'outgoing');
      const complete = !!(incoming && sc && path.inputPatchId && out.patchId && lc && outgoing);
      paths.push({
        id: `${path.id}:${out.output}`,
        incomingEndpointId: incoming,
        scEndpointId: sc ? occupancyEndpointId(sc) : null,
        splitterInEndpointId: occupancyEndpointId({
          kind: 'splitter-in',
          cassetteId: path.cassetteId,
          splitterIndex: path.splitterIndex,
        }),
        splitterOutEndpointId: occupancyEndpointId({
          kind: 'splitter-out',
          cassetteId: path.cassetteId,
          splitterIndex: path.splitterIndex,
          output: out.output,
        }),
        lcEndpointId: lc ? occupancyEndpointId(lc) : null,
        outgoingEndpointId: outgoing,
        weldIds: welds
          .filter((item) =>
            [incoming, outgoing, sc ? occupancyEndpointId(sc) : '', lc ? occupancyEndpointId(lc) : ''].includes(
              item.fromEndpointId,
            )
            || [incoming, outgoing, sc ? occupancyEndpointId(sc) : '', lc ? occupancyEndpointId(lc) : ''].includes(
              item.toEndpointId,
            ),
          )
          .map((item) => item.id),
        patchIds: [path.inputPatchId, out.patchId].filter((item): item is string => !!item),
        complete,
      });
    }
  }
  return paths;
}

function otherEnd(
  patch: OccupancyPatchConnection | null,
  pred: (end: OccupancyEndpoint) => boolean,
): OccupancyEndpoint | null {
  if (!patch) return null;
  const from = parseOccupancyEndpointId(patch.fromEndpointId);
  const to = parseOccupancyEndpointId(patch.toEndpointId);
  if (from && pred(from)) return from;
  if (to && pred(to)) return to;
  return null;
}

function weldTouching(
  welds: readonly OccupancyWeldConnection[],
  connector: OccupancyEndpoint | null,
  preferRole?: OccupancyCableRole,
): string | null {
  if (!connector || connector.kind !== 'cassette-connector') return null;
  for (const weld of welds) {
    const ends = [parseOccupancyEndpointId(weld.fromEndpointId), parseOccupancyEndpointId(weld.toEndpointId)];
    const fiber = ends.find((item) => item?.kind === 'cable-fiber') as OccupancyCableFiberEndpoint | undefined;
    const hasConn = ends.some((item) => occupancySameConnectorChannel(item, connector));
    if (!fiber || !hasConn) continue;
    if (preferRole && fiber.role && fiber.role !== preferRole) continue;
    return occupancyEndpointId(fiber);
  }
  return null;
}

export function occupancyConnectionOppositeLabel(
  layout: RozvadecWeldLayout,
  endpointId: string,
): { cassetteId: string | null; position: string; connectionType: 'weld' | 'patchcord'; cableLabel: string | null } | null {
  const query = parseOccupancyEndpointId(endpointId);
  const touches = (from: string, to: string): boolean =>
    from === endpointId
    || to === endpointId
    || occupancySameConnectorChannel(query, parseOccupancyEndpointId(from))
    || occupancySameConnectorChannel(query, parseOccupancyEndpointId(to));
  const weld = layoutWeldConnections(layout).find((item) => touches(item.fromEndpointId, item.toEndpointId));
  const patch = layoutPatchConnections(layout).find((item) => touches(item.fromEndpointId, item.toEndpointId));
  const row = weld ?? patch;
  if (!row) return null;
  const fromParsed = parseOccupancyEndpointId(row.fromEndpointId);
  const other =
    row.fromEndpointId === endpointId || occupancySameConnectorChannel(query, fromParsed)
      ? row.toEndpointId
      : row.fromEndpointId;
  const parsed = parseOccupancyEndpointId(other);
  if (!parsed) return null;
  if (parsed.kind === 'cable-fiber') {
    return {
      cassetteId: null,
      position: `vlákno ${parsed.physicalFiber}`,
      connectionType: weld ? 'weld' : 'patchcord',
      cableLabel: parsed.cableId,
    };
  }
  if (parsed.kind === 'cassette-connector') {
    return {
      cassetteId: parsed.cassetteId,
      position: `pozice ${parsed.slotInCassette}`,
      connectionType: weld ? 'weld' : 'patchcord',
      cableLabel: null,
    };
  }
  if (parsed.kind === 'splitter-in') {
    return {
      cassetteId: parsed.cassetteId,
      position: `splitter ${parsed.splitterIndex} / IN`,
      connectionType: 'patchcord',
      cableLabel: null,
    };
  }
  return {
    cassetteId: parsed.cassetteId,
    position: `splitter ${parsed.splitterIndex} / OUT ${parsed.output}`,
    connectionType: 'patchcord',
    cableLabel: null,
  };
}

export function occupancyIssueMessageCs(code: OccupancyConnectionIssueCode): string {
  switch (code) {
    case 'ORIENTATION_NEEDS_CABLE_ROLE':
      return 'Určete, zda je kabel vstupní nebo výstupní.';
    case 'CONNECTION_DIRECTION_UNKNOWN':
      return 'Nelze určit směr cesty — chybí vstupní vlákno.';
    case 'PATCH_PORT_OCCUPIED':
      return 'Tento kanál už má patchcord (patch-side).';
    case 'WELD_PORT_OCCUPIED':
      return 'Tento kanál už má svar (splice-side).';
    case 'WELD_FIBER_OCCUPIED':
      return 'Toto vlákno už je svařené.';
    case 'DIRECT_CABLE_CABLE_FORBIDDEN':
      return 'Přímo kabel na kabel lze svařit jen v kazetě nebo na ploše optické spojky.';
    case 'SPLITTER_OUT_OCCUPIED':
      return 'Tento výstup splitteru už je napojený na LC pozici.';
    case 'CONNECTOR_TYPE_INCOMPATIBLE':
      return 'Nekompatibilní typy konektorů.';
    case 'SPLITTER_OUT_USED_AS_IN':
      return 'Výstup splitteru nelze použít jako vstup.';
    case 'OUTGOING_AS_PATH_START':
      return 'Výstupní vlákno nemůže začínat cestu.';
    case 'SPLITTER_PATH_INCOMPLETE':
      return 'Cesta přes splitter je neúplná.';
    case 'SPLITTER_PATH_CYCLIC':
      return 'Cesta přes splitter je cyklická.';
    case 'ENDPOINT_UNKNOWN':
      return 'Neznámý koncový bod.';
  }
}
